import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, FactoryProvider, inject, ModuleWithProviders, NgModule, provideAppInitializer, Provider } from '@angular/core';
import { InterpolatableTranslationObject, TranslateStore } from '@ngx-translate/core';

import { DesktopModalComponent } from 'shared-components/desktop';
import { IconComponent } from 'shared-components/icon';
import { I18nModule, I18nService } from 'shared-modules-angular/i18n';

import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { FileBrowserComponent } from './components/file-browser/file-browser.component';
import { FileBrowserModalComponent } from './components/file-browser-modal/file-browser-modal.component';
import { FileListComponent } from './components/file-list/file-list.component';
import { OvenViewComponent } from './components/oven-view/oven-view.component';
import { SourceItemComponent } from './components/source-item/source-item.component';
import { UnauthorizedViewComponent } from './components/unauthorized-view/unauthorized-view.component';
import { DriveFileBrowserService, LocalFileBrowserService } from './services';

type ModuleConfig = {
  openLocalFileAction: FactoryProvider,
  openDriveFileAction: FactoryProvider,
  acceptedExtensions: FactoryProvider,
  envConfig: Provider,
};

@NgModule({
  declarations: [
    UnauthorizedViewComponent,
    OvenViewComponent,
    FileBrowserComponent,
    FileListComponent,
    SourceItemComponent,
    BreadcrumbComponent,
    FileBrowserModalComponent,
  ],
  imports: [
    CommonModule,
    // shared component and modules
    I18nModule.forChild(),
    IconComponent,
    DesktopModalComponent,
  ],
  bootstrap: [FileBrowserModalComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FileBrowserModule {
  public static forRoot(config: ModuleConfig): ModuleWithProviders<FileBrowserModule> {
    return {
      ngModule: FileBrowserModule,
      providers: [
        DriveFileBrowserService,
        LocalFileBrowserService,
        config.openLocalFileAction,
        config.openDriveFileAction,
        config.acceptedExtensions,
        config.envConfig,
        provideAppInitializer(() => {
          const translateStore: TranslateStore = inject(TranslateStore);
          const i18nService: I18nService = inject(I18nService);

          // Try to merge every app's supported languages with library
          i18nService.getAvailableLanguages().forEach(async (lang) => {
            try {
              const libTranslations: InterpolatableTranslationObject = await import(`./assets/i18n/${lang}.json`);
              const appTranslations: InterpolatableTranslationObject = translateStore.getTranslations(lang);

              translateStore.setTranslations(lang, libTranslations, true);
              translateStore.setTranslations(lang, appTranslations, true);
            } catch {
              // Silence is golden
            }
          });
        }),
      ],
    };
  }

  public static forChild(): ModuleWithProviders<FileBrowserModule> {
    return {
      ngModule: FileBrowserModule,
    };
  }
}
